import React from "react";
import { formatIDR } from "@/lib/api";
import { Factory, Plus, Trash2 } from "lucide-react";

const sel = "h-7 w-full border border-slate-300 rounded-md px-1.5 text-[11px] bg-white focus:outline-none focus:ring-1 focus:ring-sky-400";

const ProdRow = ({ x, p, j, catCats, vars, onSet, onCat, onDel, line, canDel }) => (
  <div className="space-y-1 rounded-md border border-sky-100 bg-white/70 p-1.5" data-testid={`de-img-prod-row-${x.id}-${j}`}>
    <div className="flex items-center gap-1">
      <span className="text-[10px] font-semibold text-sky-600 w-4 shrink-0">{j + 1}.</span>
      <select data-testid={`de-img-prod-cat-${x.id}-${j}`} value={p.catId} onChange={(e) => onCat(e.target.value)} className={sel}>
        <option value="">— kategori katalog —</option>
        {catCats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
      </select>
      {canDel && <button type="button" data-testid={`de-img-prod-del-${x.id}-${j}`} onClick={onDel} className="p-1 text-slate-400 hover:text-rose-500 shrink-0" title="Hapus biaya produksi ini"><Trash2 className="w-3.5 h-3.5" /></button>}
    </div>
    {p.catId && (
      <select data-testid={`de-img-prod-var-${x.id}-${j}`} value={p.varId} onChange={(e) => onSet({ varId: e.target.value })} className={sel}>
        {(vars || []).length === 0 && <option value="">— belum ada varian —</option>}
        {(vars || []).map((v) => <option key={v.id} value={v.id}>{v.tipe}{v.spec ? ` · ${v.spec}` : ""}</option>)}
      </select>
    )}
    <div className="flex items-center justify-between gap-2 text-[10px] text-slate-500">
      <label className="flex items-center gap-1">Qty <input type="number" min="1" data-testid={`de-img-prod-qty-${x.id}-${j}`} value={p.qty} onChange={(e) => onSet({ qty: e.target.value })} className="h-6 w-14 border border-slate-300 rounded-md px-1 text-[11px]" /></label>
      <span>{line.pct > 0 && <s className="text-slate-400 mr-1">{formatIDR(line.base)}</s>}@ {formatIDR(line.unit)} · <b className="text-sky-700" data-testid={`de-img-prod-line-cost-${x.id}-${j}`}>{formatIDR(line.cost)}</b></span>
    </div>
    {line.v && (
      <div className="flex flex-wrap items-center justify-between gap-1 text-[10px]">
        {line.expOk ? (
          <span className="inline-flex rounded-md border border-slate-300 overflow-hidden" data-testid={`de-img-prod-exp-group-${x.id}-${j}`}>
            <button type="button" data-testid={`de-img-prod-reg-${x.id}-${j}`} onClick={() => onSet({ express: false })} className={`px-2 py-0.5 font-semibold ${!line.exp ? "bg-sky-600 text-white" : "bg-white text-slate-500"}`}>REGULER</button>
            <button type="button" data-testid={`de-img-prod-exp-${x.id}-${j}`} onClick={() => onSet({ express: true })} className={`px-2 py-0.5 font-semibold ${line.exp ? "bg-amber-500 text-white" : "bg-white text-slate-500"}`}>EXPRESS</button>
          </span>
        ) : <span className="text-slate-400 italic" data-testid={`de-img-prod-noexp-${x.id}-${j}`}>Kategori ini tanpa Express</span>}
        <span className="text-slate-500" data-testid={`de-img-prod-lead-${x.id}-${j}`}>
          {line.exp && <b className="text-amber-600">+{formatIDR(line.surcharge)} · </b>}
          {line.lead ? (line.exp ? `±${line.lead} hari berjalan` : `±${line.lead} hari kerja`) : "lead time belum diatur"}
        </span>
      </div>
    )}
    {(line.pct > 0 || line.next) && (
      <div className="flex flex-wrap items-center gap-1 text-[10px]">
        {line.pct > 0 && <span className="px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 font-semibold" data-testid={`de-img-prod-tier-${x.id}-${j}`}>Diskon tier {line.pct}% · hemat {formatIDR((line.base - line.unit) * (+p.qty || 1))}</span>}
        {line.next && <button type="button" onClick={() => onSet({ qty: line.next.qty })} className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200 hover:bg-emerald-100" data-testid={`de-img-prod-next-${x.id}-${j}`}>Pesan {line.next.qty} pcs → diskon {line.next.pct}%</button>}
      </div>
    )}
  </div>
);

export const ImgProduction = ({ x, catCats, varsByCat, onToggle, onSetProd, onCat, onAdd, onDel, lines, cost }) => (
  <div className="mt-2 rounded-lg border border-sky-200 bg-sky-50/40 p-2" data-testid={`de-img-prod-${x.id}`}>
    <div className="flex items-center justify-between">
      <span className="flex items-center gap-1 text-[11px] font-semibold text-sky-700"><Factory className="w-3 h-3" /> Biaya Produksi</span>
      <button type="button" data-testid={`de-img-prod-toggle-${x.id}`} onClick={onToggle}
        className={`w-9 h-5 rounded-full relative transition-colors ${x.prodOn ? "bg-sky-500" : "bg-slate-300"}`}>
        <span className={`absolute top-0.5 left-0 w-4 h-4 rounded-full bg-white shadow transition-transform ${x.prodOn ? "translate-x-[18px]" : "translate-x-0.5"}`} />
      </button>
    </div>
    {x.prodOn && (
      <div className="mt-1.5 space-y-1.5">
        {x.prods.map((p, j) => (
          <ProdRow key={p.key} x={x} p={p} j={j} catCats={catCats} vars={varsByCat[p.catId]} canDel={x.prods.length > 1}
            onSet={(patch) => onSetProd(j, patch)} onCat={(c) => onCat(j, c)} onDel={() => onDel(j)} line={lines[j] || { unit: 0, cost: 0, base: 0, pct: 0 }} />
        ))}
        <button type="button" data-testid={`de-img-prod-add-${x.id}`} onClick={onAdd} className="w-full flex items-center justify-center gap-1 h-7 rounded-md border border-dashed border-sky-300 text-[11px] font-medium text-sky-700 hover:bg-sky-50">
          <Plus className="w-3.5 h-3.5" /> Tambah biaya produksi
        </button>
        <div className="flex items-center justify-between border-t border-sky-200 pt-1 text-[11px]">
          <span className="text-sky-700 font-semibold">Biaya Produksi{x.prods.length > 1 ? ` (${x.prods.length} item)` : ""}</span>
          <b className="text-sky-700" data-testid={`de-img-prod-cost-${x.id}`}>{formatIDR(cost)}</b>
        </div>
      </div>
    )}
  </div>
);
